import {
  createAction,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityState,
} from '@reduxjs/toolkit';
import { v4 } from 'uuid';

import { CONTRACT_SETUP_ALL_STATE_CLEAR } from '@app/store/actionTypes';
import { contractSetupByDraftSuccess } from '@app/store/contract-setup/actions';
import LocaleCode from '@app/store/LocaleCode';
import { RootState } from '@app/store/types';

export const phoneNumberRegex = /([0-9]{3})([0-9]{4})([0-9]+)/;
export type CarbonCopyInputAddBy = 'self' | 'addressBook';
export type CarbonCopyForm = Dictionary<CarbonCopy>;
export const carbonCopyLocale = [
  { language: LocaleCode.KOREAN, displayName: '한국어' },
  { language: LocaleCode.ENGLISH, displayName: '영어' },
  { language: LocaleCode.CHINESE, displayName: '중국어' },
  { language: LocaleCode.JAPANESE, displayName: '일본어' },
  { language: LocaleCode.VIETNAMESE, displayName: '베트남어' },
];

export interface CarbonCopy {
  addressBookName: string | null;
  contact: string; // email or phoneNumber : example@example.com , 01098765432
  id: string;
  inputAddBy: CarbonCopyInputAddBy;
  locale: LocaleCode;
}

const carbonCopyEntityAdapter = createEntityAdapter<CarbonCopy>();

export type CarbonCopyContact = Pick<CarbonCopy, 'contact' | 'locale'>;

interface CarbonCopiesState extends EntityState<CarbonCopy> {}

const initialState: CarbonCopiesState = carbonCopyEntityAdapter.getInitialState();

export const addCarbonCopyList = createAction<CarbonCopy[]>('carbonCopies/addCarbonCopyList');
export const replaceCarbonCopyList = createAction<CarbonCopy[]>(
  'carbonCopies/replaceCarbonCopyList'
);

export const getCarbonCopyState = (state: RootState) => state.contractSetup.carbonCopies;

export const getCarbonCopyIds = (state: RootState) => getCarbonCopyState(state).ids;
export const getCarbonCopyEntities = (state: RootState) => getCarbonCopyState(state).entities;

export const getCarbonCopiesData = (state: RootState): Array<CarbonCopy> => {
  return Object.values(getCarbonCopyEntities(state)).filter((item): item is CarbonCopy => !!item);
};
export const getCarbonCopiesContactArray = (state: RootState): CarbonCopyContact[] => {
  return getCarbonCopiesData(state)
    .filter((item): item is CarbonCopy => !!item)
    .map(({ contact, locale }) => {
      return { contact, locale };
    });
};

export const { reducer } = createSlice({
  name: 'carbonCopies',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(addCarbonCopyList, (state, action) => {
      carbonCopyEntityAdapter.addMany(state, action.payload);
    });
    builder.addCase(replaceCarbonCopyList, (state, action) => {
      carbonCopyEntityAdapter.removeAll(state);
      carbonCopyEntityAdapter.addMany(state, action.payload);
    });

    builder.addCase(CONTRACT_SETUP_ALL_STATE_CLEAR, () => {
      return initialState;
    });
    builder.addCase(contractSetupByDraftSuccess, (state, action) => {
      carbonCopyEntityAdapter.removeAll(state);
      const { carbonCopies } = action.payload;
      if (carbonCopies) {
        carbonCopyEntityAdapter.addMany(
          state,
          carbonCopies.map(({ contact, locale }) => ({
            addressBookName: null,
            contact,
            id: v4(),
            inputAddBy: 'self',
            locale,
          }))
        );
      }
    });
  },
});

export default reducer;
